// Pure matching/display helpers. Time overlap is a suggestion, never consent.
export function healthWorkoutWindow(execution, now = Date.now()) {
  const explicitStart = Date.parse(execution?.startedAt)
  const explicitEnd = Date.parse(execution?.endedAt)
  const timestamp = Number(execution?.ts) * 1000
  const recordedEnd = timestamp > 0 ? timestamp : Date.parse(execution?.createdAt)
  // A date edited in the hub takes precedence over the original recording date.
  const explicitMatchesDate = !Number.isFinite(recordedEnd) || !Number.isFinite(explicitEnd)
    || new Date(recordedEnd).toDateString() === new Date(explicitEnd).toDateString()
  if (Number.isFinite(explicitStart) && explicitEnd > explicitStart && explicitMatchesDate) {
    return { from: explicitStart, to: explicitEnd, exact: true }
  }
  const watchStart = Date.parse(execution?.watchData?.startedAt)
  const watchEnd = Date.parse(execution?.watchData?.endedAt)
  if (Number.isFinite(watchStart) && watchEnd > watchStart) return { from: watchStart, to: watchEnd, exact: true }
  const to = Number.isFinite(recordedEnd) && recordedEnd > 0 ? recordedEnd : now
  const duration = Math.min(7 * 86400, Math.max(0, Number(execution?.duration) || 0)) * 1000
  return { from: to - duration, to, exact: false }
}

export function healthSearchDay(date) {
  // Local calendar boundaries also handle daylight-saving changes and midnight.
  const start = new Date(date + "T00:00:00")
  const end = new Date(start)
  end.setDate(end.getDate() + 1)
  return { from: start.getTime(), to: end.getTime() }
}

export function rankHealthWorkouts(workouts, execution) {
  const window = healthWorkoutWindow(execution)
  const duration = Math.max(60000, window.to - window.from)
  const family = execution.healthActivityFamily || (execution.hyroxSegments?.length ? "mixed" : Number(execution.liftedWeight) > 0 ? "strength" : null)
  return workouts.map((workout) => {
    const start = Date.parse(workout.startedAt)
    const end = Date.parse(workout.endedAt)
    const overlap = Math.max(0, Math.min(end, window.to) - Math.max(start, window.from))
    const union = Math.max(end, window.to) - Math.min(start, window.from)
    const proximity = Math.abs(end - window.to) + Math.abs(start - window.from)
    const durationDifference = Math.abs((Number(workout.durationSeconds) || 0) * 1000 - duration)
    const strongMatch = window.exact && union > 0 && overlap / union >= 0.65 && proximity <= 30 * 60000
    return { ...workout, strongMatch, overlapMinutes: Math.round(overlap / 60000),
      matchScore: (union > 0 ? overlap / union * 100 : 0) - proximity / 60000 / 10 - durationDifference / 60000 / 20 + (family && workout.activityFamily === family ? 8 : 0) }
  }).sort((a, b) => b.matchScore - a.matchScore || Date.parse(b.startedAt) - Date.parse(a.startedAt))
}

export function formatHealthDuration(seconds) {
  const value = Math.max(0, Math.round(Number(seconds) || 0))
  const hours = Math.floor(value / 3600)
  const minutes = Math.floor(value % 3600 / 60)
  return hours ? `${hours} h ${minutes} min` : `${minutes}:${String(value % 60).padStart(2, "0")}`
}

export function healthPulsePlot(workout) {
  const samples = workout?.heartRateSamples || []
  if (workout?.heartRateStatus !== "samples" || samples.length < 3) return { paths: [], dots: [] }
  const elapsed = (Date.parse(workout.endedAt) - Date.parse(workout.startedAt)) / 1000
  const values = samples.map((point) => point.bpm)
  const low = Math.min(...values) - 8, high = Math.max(...values) + 8
  const paths = [], dots = []
  let line = [], previous = null
  samples.forEach((sample) => {
    const point = { x: 4 + sample.offsetSeconds / Math.max(1, elapsed) * 312, y: 68 - (sample.bpm - low) / (high - low) * 60 }
    if (previous !== null && sample.offsetSeconds - previous > 90) {
      if (line.length > 1) paths.push(line.join(" "))
      line = []
    }
    line.push(`${point.x.toFixed(2)},${point.y.toFixed(2)}`)
    dots.push(point)
    previous = sample.offsetSeconds
  })
  if (line.length > 1) paths.push(line.join(" "))
  return { paths, dots }
}
